import { Fragment, useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"
import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"

import { getIcon } from "utils/icon"

import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { useDining } from "hooks/dining/useDining"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { Head } from "components/Head"
import { SearchToggleButton } from "components/domains/search"
import { LanguageButton } from "components/Language"
import { ImageSkeletonCard } from "components/ImageCard"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { DiningIcon } from "components/icons"
import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { MotionDiv, showContent } from "components/motions"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"
import { DiningCard } from "components/domains/dining/DiningCard"

const DEFAULT_FILTER_ICON = <DiningIcon />
const ALL_FILTER_ICON = DEFAULT_FILTER_ICON

export const DiningKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { openSearch } = useAppUI()
  const {
    restaurants,
    currentCategory,
    categories,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasData,
    hasNextPage,
    endScrollRef,
    filterByCategoryId,
    filterByVenueId,
  } = useDining()

  const { t } = useTranslation({ defaultValue: null })

  const handleChangeCategory = useCallback(
    (e, id) => {
      if (id === null) return
      filterByCategoryId(id)
    },
    [filterByCategoryId]
  )

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.dining) || <DiningIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`AppDining`] || {}
  const {
    root,
    filterSection,
    filterContainer,
    diningImageCard,
    filterButton,
    gridContainer,
    emptyDataContainer,
    navigationWrapper,
    venueSelector,
    toolbarContainer,
    actionContainer,
    actionButton,
    searchBarContainer,
    languageSelectMenu,
  } = styleOverrides

  return (
    <>
      <Head title={t("Dining")} />
      <Layout>
        <Box sx={root}>
          {hasData ? (
            <MotionDiv
              animate="animate"
              initial="initial"
              variants={showContent}
              sx={gridContainer}
            >
              {restaurants.pages.map((page, pageNum) => {
                return (
                  <Fragment key={pageNum}>
                    {page.data.map((restaurant, index) => {
                      return (
                        <DiningCard
                          data={restaurant}
                          sx={diningImageCard}
                          key={`dining-${index}`}
                        />
                      )
                    })}
                  </Fragment>
                )
              })}
              {hasNextPage &&
                Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                  <ImageSkeletonCard
                    ref={index === SKELETON_INDEX_TO_REF ? endScrollRef : null}
                    key={index}
                    sx={diningImageCard}
                  />
                ))}
            </MotionDiv>
          ) : (
            <MotionDiv
              animate="animate"
              initial="initial"
              variants={showContent}
              sx={emptyDataContainer}
            >
              <EmptyData
                icon={emptyIcon}
                primary={t("Coming soon")}
                secondary={t("Stay tuned for the update")}
              />
            </MotionDiv>
          )}
          {categories.length > 0 && (
            <Box sx={filterSection}>
              <HorizontalScrollPane
                className="subCategory-filterbar"
                stackProps={{
                  sx: filterContainer,
                }}
              >
                <CategoryFilterBar
                  sx={filterButton}
                  activeCategory={currentCategory}
                  categories={categories}
                  allButtonIcon={ALL_FILTER_ICON}
                  defaultButtonIcon={DEFAULT_FILTER_ICON}
                  onChange={handleChangeCategory}
                  renderFilterButton={CategoryFilterChip}
                />
              </HorizontalScrollPane>
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={filterByVenueId}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageSelectMenu sx={languageSelectMenu} />
                <LanguageButton variant="contained" sx={actionButton} />
                {/* //TODO:: Add a wheel chair's mode handler function later */}
                {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
                {additionalMenu}
                {/* //TODO:: Create Toolbar component and refactor Code */}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
